import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import apiClient from '@/services/apiClient'
import {
  MAX_OFFSET_MS,
  RealDate,
  TIME_TRAVEL_ENABLED,
  setTimeOffsetMs,
  timeOffsetMs,
} from '@/utils/timeTravel'
import '@/styles/components/time-travel-bar.css'

/** A Date as the value a datetime-local input takes: "2026-12-18T16:58". */
const toInputValue = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** "46 days ahead", "3 hours ahead". */
const aheadLabel = (ms: number): string => {
  const days = Math.floor(ms / 86_400_000)
  if (days >= 1) return `${days} ${days === 1 ? 'day' : 'days'} ahead`
  const hours = Math.max(1, Math.round(ms / 3_600_000))
  return `${hours} ${hours === 1 ? 'hour' : 'hours'} ahead`
}

/**
 * DF-515: the testers' time machine, a thin bar across the top of the portal
 * outside production. Shows the portal's current time (ticking) and lets a
 * tester jump forward up to 89 days, or go back to now. Each change asks the
 * backend to refresh the customer's order from Dynamics, then reloads the
 * page so everything runs at the new time.
 */
const TimeTravelBar: React.FC = () => {
  const location = useLocation()
  const offset = timeOffsetMs()
  const [now, setNow] = useState(() => new Date())
  const [target, setTarget] = useState(() => toInputValue(new Date()))
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  // Not in production, and not inside the Dynamics view.
  if (!TIME_TRAVEL_ENABLED || location.pathname.startsWith('/crm')) return null

  const realNow = new RealDate(RealDate.now())
  const min = toInputValue(realNow)
  const max = toInputValue(new RealDate(RealDate.now() + MAX_OFFSET_MS))

  const apply = async (newOffset: number) => {
    setBusy(true)
    setTimeOffsetMs(newOffset)
    // Have the order fetched fresh from Dynamics at the new time. Not signed
    // in, or it fails: the page still reloads at the new time.
    await apiClient.post('/api/auth/time-travel/reset', {}).catch(() => undefined)
    window.location.reload()
  }

  const jump = () => {
    const chosen = new RealDate(target).getTime()
    if (Number.isNaN(chosen)) return
    void apply(Math.min(Math.max(chosen - RealDate.now(), 0), MAX_OFFSET_MS))
  }

  return (
    <div className={`time-travel-bar${offset > 0 ? ' is-travelling' : ''}`} role="region" aria-label="Test clock">
      <span className="time-travel-label">Test clock</span>
      <span className="time-travel-now">
        {now.toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        {offset > 0 && <span className="time-travel-ahead"> ({aheadLabel(offset)})</span>}
      </span>
      <span className="time-travel-controls">
        <input
          type="datetime-local"
          className="time-travel-input"
          value={target}
          min={min}
          max={max}
          onChange={e => setTarget(e.target.value)}
          aria-label="Jump to"
          disabled={busy}
        />
        <button type="button" className="time-travel-btn" onClick={jump} disabled={busy || !target}>
          Go
        </button>
        {offset > 0 && (
          <button type="button" className="time-travel-btn is-secondary" onClick={() => void apply(0)} disabled={busy}>
            Back to now
          </button>
        )}
      </span>
    </div>
  )
}

export default TimeTravelBar
